import React, { useState, useEffect } from 'react';
import { Check, X, CreditCard, Zap, Crown, Building2 } from 'lucide-react';
import PaymentModal from './PaymentModal';
import { getProducts } from '../lib/products';
import { Product, User } from '../lib/types';
import { getCurrentUser } from '../lib/auth';

const PricingSection: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPlanPrice, setSelectedPlanPrice] = useState<string>("");
  const [products, setProducts] = useState<Product[]>(getProducts);
  const currentUser = getCurrentUser();

  useEffect(() => {
    // Listen for product updates
    const handleProductsUpdate = () => {
      setProducts(getProducts());
    };

    window.addEventListener('productsUpdated', handleProductsUpdate);
    return () => window.removeEventListener('productsUpdated', handleProductsUpdate);
  }, []);

  const handleSelectPlan = (plan: string, price: string) => {
    setSelectedPlan(plan);
    setSelectedPlanPrice(price);
    setIsPaymentModalOpen(true);
  };

  const getIconComponent = (productId: string) => {
    switch (productId) {
      case 'trial':
        return Zap;
      case 'premium':
        return Crown;
      case 'enterprise':
        return Building2;
      default:
        return Zap;
    }
  };

  const isCurrentPlan = (productRole: string): boolean => {
    return currentUser?.role === productRole;
  };

  return (
    <section id="tarifs" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Nouveau Modèle Tarifaire : Ultra-compétitif</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Choisissez le forfait qui correspond le mieux à vos besoins. Tous nos plans incluent l'accès à notre générateur d'offres d'emploi de qualité professionnelle.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {products.map((product, index) => {
            const IconComponent = getIconComponent(product.id);
            const isPopular = product.id === 'pro';
            const isCurrent = isCurrentPlan(product.role);
            
            return (
              <div 
                key={product.id}
                className={`bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 ${
                  isPopular ? 'transform scale-105 border-2 border-purple-500 relative' : ''
                } ${
                  isCurrent ? 'ring-4 ring-indigo-500 ring-opacity-50' : ''
                }`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1">
                    POPULAIRE
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute top-0 left-0 bg-indigo-500 text-white text-xs font-bold px-3 py-1">
                    VOTRE FORFAIT
                  </div>
                )}
                
                <div className="p-6 border-b">
                  <div className="flex items-center mb-2">
                    <div className={`w-8 h-8 flex items-center justify-center ${
                      isPopular ? 'text-purple-500' : 'text-blue-500'
                    } mr-3`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800">{product.name}</h3>
                  </div>
                  <div className="flex items-end mb-4">
                    <span className="text-4xl font-bold text-gray-800">{product.price}€</span>
                    <span className="text-gray-500 ml-1">/mois</span>
                  </div>
                  <p className="text-gray-600">
                    Pour les recruteurs {product.id === 'enterprise' ? 'en équipe' : 'réguliers'}
                  </p>
                </div>
                
                <div className="p-6">
                  <ul className="space-y-3">
                    {product.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <Check className="w-5 h-5 text-green-500 mr-2" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button 
                    onClick={() => handleSelectPlan(product.id, product.price)}
                    disabled={isCurrent}
                    className={`w-full mt-6 ${
                      isCurrent 
                        ? 'bg-gray-300 cursor-not-allowed'
                        : isPopular 
                          ? 'bg-purple-600 hover:bg-purple-700' 
                          : 'bg-indigo-600 hover:bg-indigo-700'
                    } text-white py-2 px-4 rounded-md transition-colors flex items-center justify-center space-x-2`}
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>
                      {isCurrent 
                        ? 'Forfait actuel'
                        : product.id === 'enterprise' 
                          ? 'Contacter les ventes' 
                          : "S'abonner maintenant"}
                    </span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal 
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        planId={selectedPlan || ""}
        planPrice={selectedPlanPrice}
      />
    </section>
  );
};

export default PricingSection;